import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Scenario } from 'api';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import { Format } from 'common/types/format';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { ScalarSpreadsheetProps } from 'components/ScalarSpreadsheet/ScalarSpreadsheet';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import { useWaterfallContext } from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import useFundInvestmentSheetConfig from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import { createRelatedEntityInputSheetConfig } from 'pages/Waterfall/SpreadsheetConfig/RelatedEntityInputSheetConfig/relatedEntityInputSheetConfig';
import useWaterfallSheetConfig from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { RelatedEntitySheet } from 'pages/Waterfall/utilities/useRelatedEntitiesWaterfalls';
import { WeightedShareValue } from '../FundFirmDistribution/FundFirmDistribution.props';
import WaterfallRecalculation from '../WaterfallRecalculation';
import WaterfallTableFormatter from '../WaterfallTableFormatter';
import WaterfallTables from '../WaterfallTables/WaterfallTables';

const WATERFALL_RELATED_ENTITY_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-input-container`;

const RelatedEntityWaterfallSheets = ({
  relatedEntity,
  scenarioResult,
  childrenCapTables,
  ownedSharesValue,
  registerChildrenSheets,
  inputSheet,
  inputFormat,
  isLoading,
  onInputChange,
}: {
  relatedEntity: ChildCompany;
  scenarioResult?: Scenario;
  childrenCapTables: CapTableVersionWithCompanyId[];
  ownedSharesValue?: WeightedShareValue[];
  registerChildrenSheets: (sheets: RelatedEntitySheet[], idKey: number) => void;
  inputSheet: ReturnType<typeof createRelatedEntityInputSheetConfig>;
  inputFormat: Format;
  isLoading: boolean;
  onInputChange: (cell: EvaluatedCell, value: unknown) => void;
}) => {
  const { formatDispatch } = useWaterfallContext();
  const [hasCompletedRequestWithoutResults, setHasCompletedRequestWithoutResults] = useState(false);
  const wasLoadingRef = useRef(isLoading);
  const validSecurities = useMemo(
    () => childrenCapTables.find(ct => Number(ct.company_id) === Number(relatedEntity.company_id))?.securities ?? [],
    [childrenCapTables, relatedEntity.company_id]
  );
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarioResult,
    validSecurities,
    companyName: relatedEntity.company_name,
    relatedEntityId: Number(relatedEntity.company_id),
  });
  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: ownedSharesValue,
    validSecurities,
    idToAssociateTo: Number(relatedEntity.company_id),
  });
  const [isResultsPresentationReady, setIsResultsPresentationReady] = useState(
    () => !isLoading && Boolean(waterfallSheet)
  );
  const lastSettledResultsRef = useRef<
    | {
        waterfallSheet: NonNullable<typeof waterfallSheet>;
        fundInvestmentSheets: typeof fundInvestmentSheets;
      }
    | undefined
  >(undefined);

  const sheets = useMemo(
    () => [waterfallSheet, ...fundInvestmentSheets].filter(sheet => !!sheet),
    [waterfallSheet, fundInvestmentSheets]
  );

  // Register the child configs in the parent workbook for cross-sheet fund totals.
  useEffect(() => {
    if (sheets.length) {
      registerChildrenSheets(sheets, Number(relatedEntity.company_id));
    }
  }, [sheets, registerChildrenSheets, relatedEntity.company_id]);

  // Keep fully presented tables mounted through Cash/Debt edits, including zero-equity recalculations.
  useEffect(() => {
    if (waterfallSheet && !isLoading && isResultsPresentationReady) {
      lastSettledResultsRef.current = { waterfallSheet, fundInvestmentSheets };
    }
  }, [fundInvestmentSheets, isLoading, isResultsPresentationReady, waterfallSheet]);

  // Let workbook evaluation and spreadsheet measurement finish before revealing new values.
  useEffect(() => {
    if (isLoading || !waterfallSheet) {
      setIsResultsPresentationReady(false);
      return undefined;
    }

    let secondAnimationFrame: number | undefined;
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      secondAnimationFrame = window.requestAnimationFrame(() => {
        setIsResultsPresentationReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame);
      if (secondAnimationFrame !== undefined) {
        window.cancelAnimationFrame(secondAnimationFrame);
      }
    };
  }, [isLoading, waterfallSheet]);

  // Distinguish a settled empty response from the brief gap before a new request starts.
  useEffect(() => {
    if (waterfallSheet || isLoading) {
      setHasCompletedRequestWithoutResults(false);
    } else if (wasLoadingRef.current) {
      setHasCompletedRequestWithoutResults(true);
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, waterfallSheet]);

  const isAwaitingResultsPresentation =
    isLoading || Boolean(waterfallSheet && !isResultsPresentationReady);
  const settledResults = useMemo(
    () => waterfallSheet && !isLoading && isResultsPresentationReady
      ? { waterfallSheet, fundInvestmentSheets }
      : lastSettledResultsRef.current,
    [fundInvestmentSheets, isLoading, isResultsPresentationReady, waterfallSheet]
  );
  // A valid edit becomes observable one render before the request loading flag.
  // Keep the last settled geometry through that gap so the page never collapses.
  const isHoldingResultsSpace =
    Boolean(settledResults) &&
    (isAwaitingResultsPresentation ||
      (!waterfallSheet && !hasCompletedRequestWithoutResults));

  return (
    <Box
      aria-busy={isAwaitingResultsPresentation || undefined}
      data-testid={`related-entity-waterfall-${relatedEntity.company_id}`}>
      <Box
        className={WATERFALL_RELATED_ENTITY_INPUT_CONTAINER_CLASS}
        data-testid={`related-entity-input-${relatedEntity.company_id}`}
        sx={{ display: 'inline-block' }}>
        <WaterfallTableFormatter />
        <ScalarSpreadsheet
          {...inputSheet}
          data={inputSheet.data ?? []}
          totalData={inputSheet.totalData ?? []}
          titleData={inputSheet.titleData ?? []}
          disableUnsavedChanges
          disableVerticalScroll
          format={inputFormat}
          formatDispatch={formatDispatch}
          onChange={onInputChange}
          selectionSummaryUsesCellCurrencyFormat
          sheet={inputSheet as unknown as ScalarSpreadsheetProps['sheet']}
          showToolbar
        />
      </Box>
      {settledResults && (waterfallSheet || isHoldingResultsSpace) ? (
        <Box
          aria-hidden={isHoldingResultsSpace || undefined}
          data-testid={`related-entity-results-${relatedEntity.company_id}`}
          sx={{ position: 'relative', width: 'fit-content', maxWidth: '100%' }}>
          <WaterfallRecalculation
            aria-hidden={isHoldingResultsSpace || undefined}
            data-testid={`related-entity-results-content-${relatedEntity.company_id}`}
            inert={isHoldingResultsSpace || undefined}
            isRecalculating={isHoldingResultsSpace}>
            <Box
              data-testid={
                isHoldingResultsSpace
                  ? `related-entity-results-placeholder-${relatedEntity.company_id}`
                  : undefined
              }>
              <WaterfallTables
                waterfallSheet={settledResults.waterfallSheet}
                fundInvestmentsSheets={settledResults.fundInvestmentSheets}
              />
            </Box>
          </WaterfallRecalculation>
        </Box>
      ) : null}
    </Box>
  );
};

export default RelatedEntityWaterfallSheets;
